<template>
  <hb-section :title="title" class="wallet-summary">
    <template v-if="showTotal" #subtitle>
      <span>
        {{ $t('influencer_panel.wallet_total_hint') }}
        <hb-money :cents="balances.total" size="sm" />
      </span>
    </template>

    <template v-if="linkToStatement" #actions>
      <router-link :to="{ name: 'influencer-panel-wallet' }" class="wallet-summary__link">
        {{ $t('influencer_panel.see_statement') }}
        <feather-icon icon="ChevronRightIcon" size="14" />
      </router-link>
    </template>

    <b-alert v-if="balances.total < 0" variant="danger" show class="p-1 mb-2">
      <small>{{ $t('influencer_panel.negative_balance') }}</small>
    </b-alert>

    <div class="wallet-summary__tiles">
      <!-- Sacar e a acao principal, entao e o unico bloco preenchido. -->
      <div class="wallet-summary__tile wallet-summary__tile--primary">
        <div class="wallet-summary__tile-label">
          <feather-icon icon="CreditCardIcon" size="15" class="text-primary" />
          {{ $t('influencer_panel.withdrawable') }}
        </div>
        <hb-money :cents="balances.withdrawable" size="xl" tone="brand" />
        <p class="wallet-summary__tile-hint">{{ $t('influencer_panel.withdrawable_hint') }}</p>
        <b-button
          v-if="showWithdrawCta"
          variant="primary"
          block
          :disabled="balances.withdrawable <= 0"
          @click="$emit('withdraw')"
        >
          {{ $t('influencer_panel.request_withdrawal') }}
        </b-button>
      </div>

      <div class="wallet-summary__tile">
        <div class="wallet-summary__tile-label">
          <feather-icon icon="ShoppingBagIcon" size="15" class="text-success" />
          {{ $t('influencer_panel.spendable') }}
        </div>
        <hb-money :cents="balances.spendable" size="lg" />
        <p class="wallet-summary__tile-hint">{{ $t('influencer_panel.spendable_hint') }}</p>
      </div>

      <div class="wallet-summary__tile">
        <div class="wallet-summary__tile-label">
          <feather-icon icon="ClockIcon" size="15" class="text-warning" />
          {{ $t('influencer_panel.pending') }}
        </div>
        <hb-money :cents="balances.pending" size="lg" />
        <p class="wallet-summary__tile-hint">{{ $t('influencer_panel.pending_hint') }}</p>
        <div v-if="nextReleaseLabel" class="wallet-summary__next">
          <feather-icon icon="ArrowRightIcon" size="13" />
          {{ nextReleaseLabel }}
        </div>
      </div>
    </div>

    <div v-if="showUpcoming && upcoming.length" class="wallet-summary__upcoming">
      <div class="wallet-summary__upcoming-title">{{ $t('influencer_panel.upcoming') }}</div>
      <div class="wallet-summary__upcoming-list">
        <div v-for="item in upcoming" :key="item.available_at" class="wallet-summary__release">
          <div class="wallet-summary__day">
            <div class="wallet-summary__day-number">{{ dayChip(item.available_at).day }}</div>
            <div class="wallet-summary__day-month">{{ dayChip(item.available_at).month }}</div>
          </div>
          <div class="wallet-summary__release-value">
            <hb-money :cents="item.amount_cents" />
          </div>
        </div>
      </div>
    </div>
  </hb-section>
</template>

<script>
import { BAlert, BButton } from 'bootstrap-vue'
import HbSection from '@/components/hubee/HbSection.vue'
import HbMoney from '@/components/hubee/HbMoney.vue'
import { dayChip, daysUntil } from '../panel'

/**
 * Os tres saldos da carteira, com as proximas liberacoes.
 *
 * Usado pelo Resumo e pela Carteira. Antes cada tela desenhava isso do seu jeito — tres
 * cards numa, uma tira achatada na outra — e os mesmos numeros pareciam coisas
 * diferentes conforme a rota.
 */
export default {
  name: 'WalletSummaryCard',
  components: {
    BAlert, BButton, HbSection, HbMoney,
  },
  props: {
    balances: { type: Object, required: true },
    upcoming: { type: Array, default: () => [] },
    title: { type: String, default: '' },
    showTotal: { type: Boolean, default: false },
    showUpcoming: { type: Boolean, default: true },
    showWithdrawCta: { type: Boolean, default: true },
    linkToStatement: { type: Boolean, default: false },
  },
  computed: {
    nextReleaseLabel() {
      if (!this.upcoming.length) return ''

      const days = daysUntil(this.upcoming[0].available_at)
      if (days === null) return ''

      return days <= 0
        ? this.$t('influencer_panel.next_release_today')
        : this.$t('influencer_panel.next_release_in', { days })
    },
  },
  methods: { dayChip },
}
</script>

<style scoped>
.wallet-summary__link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.9rem;
  font-weight: var(--fw-medium);
}

.wallet-summary__tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.wallet-summary__tile {
  flex: 1 1 220px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 1.25rem;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-card);
}

.wallet-summary__tile--primary {
  flex: 1 1 260px;
  border: none;
  background: var(--tint-primary);
  gap: 0.75rem;
}

.wallet-summary__tile-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.wallet-summary__tile-hint {
  margin: 0;
  font-size: var(--fs-sm);
  color: var(--text-body);
}

.wallet-summary__next {
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--warning);
}

.wallet-summary__upcoming {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--border-color);
}

.wallet-summary__upcoming-title {
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--text-heading);
  margin-bottom: 0.85rem;
}

.wallet-summary__upcoming-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.wallet-summary__release {
  flex: 1 1 240px;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.75rem 0.9rem;
  background: var(--surface-sunken);
  border-radius: var(--radius);
}

.wallet-summary__day {
  flex: none;
  width: 44px;
  text-align: center;
  line-height: 1.1;
}

.wallet-summary__day-number {
  font-size: 1.14rem;
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.wallet-summary__day-month {
  font-size: var(--fs-xs);
  color: var(--text-muted);
}

.wallet-summary__release-value {
  min-width: 0;
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}
</style>
